import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Locations from './pages/Locations';
import Orders from './pages/Orders';
import Picking from './pages/Picking';
import Packing from './pages/Packing';
import Receiving from './pages/Receiving';
import Shipping from './pages/Shipping';
import OperatorConsole from './pages/OperatorConsole';
import StockMoves from './pages/StockMoves';
import Reports from './pages/Reports';
import OnHandReport from './pages/OnHandReport';
import Profile from './pages/Profile';
import BulkLoadStock from './pages/BulkLoadStock';
import SkuVolumeAudit from './pages/SkuVolumeAudit';
import IntegrationSettings from './pages/IntegrationSettings';
import StockValuation from './pages/StockValuation';
import WarehouseLayoutDesigner from './pages/WarehouseLayoutDesigner';
import DiscrepancyInvestigation from './pages/DiscrepancyInvestigation';
import Transport from './pages/Transport';
import DriverApp from './pages/DriverApp';
import PublicTmsTracking from './pages/PublicTmsTracking';
import WarehouseScenarios from './pages/WarehouseScenarios';
import WarehouseOptimizer from './pages/WarehouseOptimizer';
import UsersPage from './pages/Users';
import Manufacturing from './pages/Manufacturing';
import Warehouses from './pages/Warehouses';
import Alerts from './pages/Alerts';
import Transfers from './pages/Transfers';
import TransferLedger from './pages/TransferLedger';
import FreightLaneMatrix from './pages/FreightLaneMatrix';
import Forecast from './pages/Forecast';
import MasterPlanning from './pages/MasterPlanning';
import PlanningSettings from './pages/PlanningSettings';
import WarehouseKPIs from './pages/WarehouseKPIs';
import Leaderboard from './pages/Leaderboard';
import Quarantine from './pages/Quarantine';
import Serials from './pages/Serials';
import Retention from './pages/Retention';
import Analytics from './pages/Analytics';
import AuditLog from './pages/AuditLog';
import Compliance from './pages/Compliance';
import CustomReports from './pages/CustomReports';
import CsvImport from './pages/CsvImport';
import DataHealth from './pages/DataHealth';
import MarginDashboard from './pages/MarginDashboard';
import ThreePLBilling from './pages/ThreePLBilling';
import PalletManagement from './pages/PalletManagement';
import PalletScanMode from './pages/PalletScanMode';
import TwoFactorAuth from './pages/TwoFactorAuth';
import PublishChangelog from './pages/PublishChangelog';
import LocationReports from './pages/LocationReports';
import SupplierShortageReport from './pages/SupplierShortageReport';
import CustomerPricing from './pages/CustomerPricing';
import MarginFloorAudit from './pages/MarginFloorAudit';
import GrantAddon from './pages/admin/GrantAddon';
import CreateBusiness from './pages/admin/CreateBusiness';
import RecentSignups from './pages/admin/RecentSignups';
import LocationSchema from './pages/LocationSchema';
import OrderDiscounts from './pages/OrderDiscounts';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AccountSettings from './pages/AccountSettings';
import CompanySettings from './pages/CompanySettings';
import CompetitionLanding from './pages/CompetitionLanding';
import CompetitionLogin from './pages/CompetitionLogin';
import CompetitionDashboard from './pages/CompetitionDashboard';
import CompetitionComplete from './pages/CompetitionComplete';
import CompetitionAnalytics from './pages/CompetitionAnalytics';
import CompetitionOffline from './pages/CompetitionOffline';import Broadcast from './pages/Broadcast';
import Returns from './pages/Returns';
import QuarantineReview from './pages/QuarantineReview';
import SchedulerSettings from './pages/SchedulerSettings';
import ScanSettings from './pages/ScanSettings';
import CycleCounts from './pages/CycleCounts';
import CountMode from './pages/CountMode';
import Containers from './pages/Containers';
import Dispatch from './pages/Dispatch';
import CarrierPortal from './pages/CarrierPortal';
import DriverPortal from './pages/DriverPortal';
import Drivers from './pages/Drivers';
import Signup from './pages/Signup';
import RequestDemo from './pages/RequestDemo';
import Billing from './pages/Billing';
import Pricing from './pages/Pricing';
import PricingRules from './pages/PricingRules';
import PromoCodes from './pages/PromoCodes';
import PublicQuote from './pages/PublicQuote';
import AddonMarketplace from './pages/AddonMarketplace';
import ConversionAnalytics from './pages/ConversionAnalytics';
import { Terms, Privacy, SLA } from './pages/Legal';
import Onboarding from './pages/Onboarding';
import InviteAccept from './pages/InviteAccept';
import PublicTracking from './pages/PublicTracking';
import Lookup from './pages/Lookup';
import LookupScan from './pages/LookupScan';
import FixedLocations from './pages/FixedLocations';
import Replenishments from './pages/Replenishments';
import AppearanceSettings from './pages/AppearanceSettings';
import InvoiceSettings from './pages/InvoiceSettings';
import OutstandingInvoices from './pages/OutstandingInvoices';
import CustomerCard from './pages/CustomerCard';
import CustomersList from './pages/CustomersList';
import ArFollowups from './pages/ArFollowups';
import ReceiptTemplates from './pages/ReceiptTemplates';
import ReorderSuggestions from './pages/ReorderSuggestions';
import HelpCenter from './pages/HelpCenter';
import Backorders from './pages/Backorders';
import PaletteCoachmark from './components/PaletteCoachmark';
import FeatureGate from './components/FeatureGate';
import { TrialExpiredGate } from './components/TrialBanner';
import AnnualUpsellModal from './components/AnnualUpsellModal';
import InstallPrompt from './components/InstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import { WarehouseProvider } from './context/WarehouseContext';
import { CompanyProvider } from './context/CompanyContext';
import { ThemeProvider } from './context/ThemeContext';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, requireSuperadmin }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  if (requireSuperadmin && user.platform_role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  // Soft-block: pending_payment → render upgrade card unless on /billing/pricing.
  // The AnnualUpsellModal is mounted as a sibling so it overlays both the
  // paywall AND any normal page (the modal self-gates on
  // company.status === 'pending_payment').
  return (
    <>
      <TrialExpiredGate>{children}</TrialExpiredGate>
      <AnnualUpsellModal />
      <InstallPrompt />
      <OfflineIndicator />
    </>
  );
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-brand border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const location = useLocation();
  // Emergent Auth returns the user at `<origin>/...#session_id=...`.
  // Detect it synchronously during render (NOT in useEffect) so we process
  // the session BEFORE any ProtectedRoute or `/auth/me` check can run.
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/settings/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
      <Route path="/settings/company" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><CompanySettings /></ProtectedRoute>} />
      <Route path="/competition-demo" element={<CompetitionLanding />} />
      <Route path="/competition-demo/login" element={<CompetitionLogin />} />
      <Route path="/competition-demo/dashboard" element={<ProtectedRoute><CompetitionDashboard /></ProtectedRoute>} />
      <Route path="/competition-demo/complete" element={<ProtectedRoute><CompetitionComplete /></ProtectedRoute>} />
      <Route path="/admin/competition-analytics" element={<ProtectedRoute><CompetitionAnalytics /></ProtectedRoute>} />
      <Route path="/competition-demo/offline" element={<ProtectedRoute><CompetitionOffline /></ProtectedRoute>} />      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/request-demo" element={<RequestDemo />} />
      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/sla" element={<SLA />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/carrier/:token" element={<CarrierPortal />} />
      <Route path="/driver/:token" element={<DriverPortal />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/track/:trackingNumber" element={<PublicTracking />} />
      <Route path="/tms/track/:token" element={<PublicTmsTracking />} />
      <Route path="/quote/:token" element={<PublicQuote />} />

      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
      <Route path="/cycle-counts" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer', 'picker']}><CycleCounts /></ProtectedRoute>} />
      <Route path="/cycle-counts/:id/count" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'picker']}><CountMode /></ProtectedRoute>} />
      <Route path="/containers" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer', 'picker', 'packer']}><FeatureGate feature="containers"><Containers /></FeatureGate></ProtectedRoute>} />
      <Route path="/dispatch" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'packer', 'viewer']}><FeatureGate feature="dispatch"><Dispatch /></FeatureGate></ProtectedRoute>} />
      <Route path="/drivers" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><Drivers /></ProtectedRoute>} />
      <Route path="/locations" element={<ProtectedRoute><Locations /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
      <Route path="/picking" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'picker']}><Picking /></ProtectedRoute>} />
      <Route path="/packing" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'packer']}><Packing /></ProtectedRoute>} />
      <Route path="/receiving" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><Receiving /></ProtectedRoute>} />
      <Route path="/receipt-templates" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><ReceiptTemplates /></ProtectedRoute>} />
      <Route path="/reorder-suggestions" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><ReorderSuggestions /></ProtectedRoute>} />
      <Route path="/shipping" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'packer']}><Shipping /></ProtectedRoute>} />
      <Route path="/operator" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'picker', 'packer']}><OperatorConsole /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/reports/on-hand" element={<ProtectedRoute><OnHandReport /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/inventory/bulk-load" element={<ProtectedRoute><BulkLoadStock /></ProtectedRoute>} />
      <Route path="/inventory/sku-volume-audit" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><SkuVolumeAudit /></ProtectedRoute>} />
      <Route path="/settings/integrations" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><IntegrationSettings /></ProtectedRoute>} />
      <Route path="/reports/stock-valuation" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><StockValuation /></ProtectedRoute>} />
      <Route path="/warehouse-layout" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><WarehouseLayoutDesigner /></ProtectedRoute>} />
      <Route path="/warehouse-layout/scenarios" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><WarehouseScenarios /></ProtectedRoute>} />
      <Route path="/warehouse-layout/optimizer" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><WarehouseOptimizer /></ProtectedRoute>} />
      <Route path="/manufacturing" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FeatureGate feature="manufacturing"><Manufacturing /></FeatureGate></ProtectedRoute>} />
      <Route path="/transfers" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'picker']}><FeatureGate feature="transfers"><Transfers /></FeatureGate></ProtectedRoute>} />
      <Route path="/transfers/ledger" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><FeatureGate feature="transfers"><TransferLedger /></FeatureGate></ProtectedRoute>} />
      <Route path="/transfers/freight-matrix" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FeatureGate feature="transfers"><FreightLaneMatrix /></FeatureGate></ProtectedRoute>} />
      <Route path="/stock-moves" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'picker']}><StockMoves /></ProtectedRoute>} />
      <Route path="/forecast" element={<ProtectedRoute><FeatureGate feature="forecasting"><Forecast /></FeatureGate></ProtectedRoute>} />
      <Route path="/master-planning" element={<ProtectedRoute><FeatureGate feature="forecasting"><MasterPlanning /></FeatureGate></ProtectedRoute>} />
      <Route path="/planning-settings" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FeatureGate feature="forecasting"><PlanningSettings /></FeatureGate></ProtectedRoute>} />
      <Route path="/warehouse-kpis" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FeatureGate feature="warehouse_kpis"><WarehouseKPIs /></FeatureGate></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><FeatureGate feature="leaderboard"><Leaderboard /></FeatureGate></ProtectedRoute>} />
      <Route path="/quarantine" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FeatureGate feature="quarantine"><Quarantine /></FeatureGate></ProtectedRoute>} />
      <Route path="/serials" element={<ProtectedRoute><Serials /></ProtectedRoute>} />
      <Route path="/discrepancies" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><DiscrepancyInvestigation /></ProtectedRoute>} />
      <Route path="/transport" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><Transport /></ProtectedRoute>} />
      <Route path="/driver" element={<ProtectedRoute><DriverApp /></ProtectedRoute>} />
      <Route path="/driver/t/:token" element={<DriverApp />} />
      <Route path="/retention" element={<ProtectedRoute allowedRoles={['admin']}><FeatureGate feature="retention"><Retention /></FeatureGate></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><FeatureGate feature="analytics"><Analytics /></FeatureGate></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute allowedRoles={['admin']}><FeatureGate feature="audit_log"><AuditLog /></FeatureGate></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute allowedRoles={['admin']}><FeatureGate feature="compliance"><Compliance /></FeatureGate></ProtectedRoute>} />
      <Route path="/custom-reports" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FeatureGate feature="custom_reports"><CustomReports /></FeatureGate></ProtectedRoute>} />
      <Route path="/csv-import" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FeatureGate feature="csv_import"><CsvImport /></FeatureGate></ProtectedRoute>} />
      <Route path="/data-health" element={<ProtectedRoute allowedRoles={['admin']}><DataHealth /></ProtectedRoute>} />
      <Route path="/margins" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><MarginDashboard /></ProtectedRoute>} />
      <Route path="/3pl-billing" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FeatureGate feature="3pl_billing"><ThreePLBilling /></FeatureGate></ProtectedRoute>} />
      <Route path="/pallets" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer', 'picker', 'packer']}><PalletManagement /></ProtectedRoute>} />
      <Route path="/pallets/scan" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'picker', 'packer']}><PalletScanMode /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute><TwoFactorAuth /></ProtectedRoute>} />
      <Route path="/settings/appearance" element={<ProtectedRoute><AppearanceSettings /></ProtectedRoute>} />
      <Route path="/settings/invoices" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><InvoiceSettings /></ProtectedRoute>} />
      <Route path="/settings/pricing" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><PricingRules /></ProtectedRoute>} />
      <Route path="/settings/promo-codes" element={<ProtectedRoute requireSuperadmin><PromoCodes /></ProtectedRoute>} />
      <Route path="/settings/order-discounts" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><OrderDiscounts /></ProtectedRoute>} />
      <Route path="/settings/location-schema" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><LocationSchema /></ProtectedRoute>} />
      <Route path="/invoices/outstanding" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><OutstandingInvoices /></ProtectedRoute>} />
      <Route path="/invoices/customers" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><CustomersList /></ProtectedRoute>} />
      <Route path="/invoices/customer/:email" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><CustomerCard /></ProtectedRoute>} />
      <Route path="/ar/followups" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><ArFollowups /></ProtectedRoute>} />
      <Route path="/release-notes" element={<ProtectedRoute allowedRoles={['admin']}><PublishChangelog /></ProtectedRoute>} />
      <Route path="/location-reports" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><FeatureGate feature="location_reports"><LocationReports /></FeatureGate></ProtectedRoute>} />
      <Route path="/reports/supplier-shortages" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><SupplierShortageReport /></ProtectedRoute>} />
      <Route path="/settings/customer-pricing" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><CustomerPricing /></ProtectedRoute>} />
      <Route path="/reports/margin-floor-audit" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><MarginFloorAudit /></ProtectedRoute>} />
      <Route path="/admin/grant-addon" element={<ProtectedRoute requireSuperadmin><GrantAddon /></ProtectedRoute>} />
      <Route path="/admin/create-business" element={<ProtectedRoute requireSuperadmin><CreateBusiness /></ProtectedRoute>} />
      <Route path="/admin/recent-signups" element={<ProtectedRoute requireSuperadmin><RecentSignups /></ProtectedRoute>} />
      <Route path="/broadcast" element={<ProtectedRoute allowedRoles={['admin']}><FeatureGate feature="broadcast"><Broadcast /></FeatureGate></ProtectedRoute>} />
      <Route path="/returns" element={<ProtectedRoute><FeatureGate feature="returns"><Returns /></FeatureGate></ProtectedRoute>} />
      <Route path="/quarantine-review" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><QuarantineReview /></ProtectedRoute>} />
      <Route path="/admin/scheduler" element={<ProtectedRoute requireSuperadmin><SchedulerSettings /></ProtectedRoute>} />
      <Route path="/admin/scan-settings" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><ScanSettings /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><Alerts /></ProtectedRoute>} />
      <Route path="/warehouses" element={<ProtectedRoute allowedRoles={['admin']}><Warehouses /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute allowedRoles={['admin']}><UsersPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/settings/billing/addons" element={<ProtectedRoute><AddonMarketplace /></ProtectedRoute>} />
      <Route path="/admin/conversion-analytics" element={<ProtectedRoute requireSuperadmin><ConversionAnalytics /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/lookup" element={<ProtectedRoute><Lookup /></ProtectedRoute>} />
      <Route path="/lookup/scan" element={<ProtectedRoute><LookupScan /></ProtectedRoute>} />
      <Route path="/fixed-locations" element={<ProtectedRoute><FixedLocations /></ProtectedRoute>} />
      <Route path="/replenishments" element={<ProtectedRoute><Replenishments /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
      <Route path="/backorders" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'viewer']}><Backorders /></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CompanyProvider>
          <WarehouseProvider>
            <BrowserRouter>
              <ErrorBoundary>
                <AppRoutes />
                <PaletteCoachmark />
                <Toaster position="top-right" />
              </ErrorBoundary>
            </BrowserRouter>
          </WarehouseProvider>
        </CompanyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
